/**
 * Connectivity events: site up/down and tunnel re-establish, newest first.
 *
 * The retention note is not decoration. **Cato keeps only three days of
 * events** — anything NOVA has not collected inside that window is gone
 * permanently, and today's collector holds them in the backend process rather
 * than a database, so a restart loses them too. The caption says so rather than
 * letting an empty feed read as "nothing happened".
 */

import { Clock, Icon, Radio, TriangleAlert, Waypoints, Wifi, WifiOff } from "@norican/icons";
import { Alert, EmptyState, Timeline } from "@norican/react";

import type { NetworkEvent } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatTimestamp } from "./status";

/** Words in an event message that mark it as a recovery rather than a failure. */
const RECOVERY_MARKERS = ["up", "connected", "established", "restored", "online"];
/** Words that mark an event as a failure. */
const FAILURE_MARKERS = ["down", "disconnect", "fail", "lost", "timeout", "offline"];

type EventTone = "success" | "warning" | "danger" | "accent" | "default";

interface EventFeedProps {
  /** Events from `/network/overview`, newest first. */
  events: NetworkEvent[];
  /** Days of history Cato retains — quoted verbatim in the caption. */
  retentionDays: number;
}

export function EventFeed({ events, retentionDays }: EventFeedProps) {
  return (
    <PanelCard
      title="Connectivity events"
      caption={`Cato retains ${retentionDays} days — older events are not recoverable`}
    >
      {events.length === 0 ? (
        <div className="flex flex-col gap-4">
          <EmptyState
            size="sm"
            icon={<Icon icon={Clock} size="lg" />}
            title="No connectivity events collected"
            description="Either nothing has happened in the collected window, or NOVA has not run a collection yet."
          />
          <Alert tone="info" icon={<Icon icon={TriangleAlert} size="sm" />}>
            Cato only keeps {retentionDays} days of events, and NOVA currently holds what it
            collects in memory. Events are lost on a backend restart until the collector is given a
            durable store.
          </Alert>
        </div>
      ) : (
        <Timeline
          items={events.map((event) => ({
            id: event.id,
            title: event.message ?? describe(event),
            timestamp: formatTimestamp(event.occurred_at),
            description: [event.site_name, event.event_sub_type].filter(Boolean).join(" · "),
            tone: eventTone(event),
            icon: <Icon icon={eventIcon(event)} size="sm" />,
          }))}
        />
      )}
    </PanelCard>
  );
}

/** A readable title for a record that carried no message field. */
function describe(event: NetworkEvent): string {
  return [event.event_type, event.event_sub_type].filter(Boolean).join(" · ") || "Network event";
}

/** Map an event onto a marker tone by reading its message, then its severity. */
function eventTone(event: NetworkEvent): EventTone {
  const text = `${event.message ?? ""} ${event.event_sub_type ?? ""}`.toLowerCase();
  if (FAILURE_MARKERS.some((marker) => text.includes(marker))) {
    return "danger";
  }
  if (RECOVERY_MARKERS.some((marker) => text.includes(marker))) {
    return "success";
  }
  const severity = (event.severity ?? "").toLowerCase();
  if (severity === "high" || severity === "critical") {
    return "danger";
  }
  return severity === "medium" ? "warning" : "accent";
}

/** Pick a marker icon that matches the tone the event resolved to. */
function eventIcon(event: NetworkEvent) {
  const tone = eventTone(event);
  if (tone === "danger") {
    return WifiOff;
  }
  if (tone === "success") {
    return Wifi;
  }
  return event.event_sub_type ? Radio : Waypoints;
}
